/**
 * FORMATTERS UTILITY
 *
 * Utility functions for formatting data for display.
 * Includes date/time formatting, file size formatting, and file type detection.
 */

/**
 * FORMAT RELATIVE TIME
 *
 * Converts a date string to relative time format (e.g., "2 hours ago", "1 day ago").
 * Used for displaying comment and attachment timestamps.
 *
 * @param {string} dateString - ISO 8601 date string from API
 * @returns {string} Relative time string
 */
export function formatRelativeTime(dateString) {
  if (!dateString) return ''

  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now - date) / 1000)

  // Less than a minute
  if (diffInSeconds < 60) {
    return 'just now'
  }

  // Less than an hour
  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return diffInMinutes === 1 ? '1 minute ago' : `${diffInMinutes} minutes ago`
  }

  // Less than a day
  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`
  }

  // Less than a week
  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`
  }

  // Less than a month
  const diffInWeeks = Math.floor(diffInDays / 7)
  if (diffInWeeks < 4) {
    return diffInWeeks === 1 ? '1 week ago' : `${diffInWeeks} weeks ago`
  }

  // Less than a year
  const diffInMonths = Math.floor(diffInDays / 30)
  if (diffInMonths < 12) {
    return diffInMonths === 1 ? '1 month ago' : `${diffInMonths} months ago`
  }

  // More than a year
  const diffInYears = Math.floor(diffInDays / 365)
  return diffInYears === 1 ? '1 year ago' : `${diffInYears} years ago`
}

/**
 * FORMAT FILE SIZE
 *
 * Converts file size from MB (as returned by API) to human-readable format.
 * API returns fileSize in MB, so we handle KB for small files and MB for larger ones.
 *
 * @param {number} sizeInMB - File size in megabytes from API
 * @returns {string} Formatted file size (e.g., "256 KB", "10.5 MB")
 */
export function formatFileSize(sizeInMB) {
  if (sizeInMB === null || sizeInMB === undefined) return ''

  // Convert to KB for comparison
  const sizeInKB = sizeInMB * 1024

  // If less than 1 MB, show in KB
  if (sizeInMB < 1) {
    // Round to whole KB
    const kb = Math.round(sizeInKB)
    return `${kb} KB`
  }

  // Show in MB with 1 decimal place
  return `${sizeInMB.toFixed(1)} MB`
}

/**
 * IS IMAGE FILE
 *
 * Checks if a file is an image based on its MIME content type.
 * Used to determine if preview button should be shown for attachments.
 *
 * @param {string} contentType - MIME type of the file (e.g., "image/png")
 * @returns {boolean} True if file is an image
 */
export function isImageFile(contentType) {
  if (!contentType) return false
  return contentType.startsWith('image/')
}

/**
 * GET FILE EXTENSION
 *
 * Extracts file extension from filename.
 *
 * @param {string} fileName - Name of the file
 * @returns {string} File extension in lowercase (e.g., "pdf", "png")
 */
export function getFileExtension(fileName) {
  if (!fileName) return ''
  const parts = fileName.split('.')
  if (parts.length < 2) return ''
  return parts[parts.length - 1].toLowerCase()
}

/**
 * IS BLOCKED FILE TYPE
 *
 * Checks if a file extension is in the blocked list.
 * Blocked extensions: .exe, .bat, .sh, .jar, .dll, .msi, .ps1, .vbs
 *
 * @param {string} fileName - Name of the file to check
 * @returns {boolean} True if file type is blocked
 */
export function isBlockedFileType(fileName) {
  const blockedExtensions = ['exe', 'bat', 'sh', 'jar', 'dll', 'msi', 'ps1', 'vbs']
  const extension = getFileExtension(fileName)
  return blockedExtensions.includes(extension)
}

/**
 * MAX FILE SIZE CONSTANT
 * Maximum allowed file size for uploads (50 MB)
 */
export const MAX_FILE_SIZE_MB = 50

/**
 * VALIDATE FILE SIZE
 *
 * Checks if a file exceeds the maximum allowed size.
 *
 * @param {File} file - File object from input
 * @returns {boolean} True if file size is valid (within limit)
 */
export function validateFileSize(file) {
  if (!file) return false
  // Convert bytes to MB
  const fileSizeInMB = file.size / (1024 * 1024)
  return fileSizeInMB <= MAX_FILE_SIZE_MB
}

/**
 * WAS COMMENT EDITED
 *
 * Checks if a comment was edited by comparing createdOn and updatedOn timestamps.
 *
 * @param {string} createdOn - ISO 8601 date string
 * @param {string} updatedOn - ISO 8601 date string
 * @returns {boolean} True if comment was edited
 */
export function wasCommentEdited(createdOn, updatedOn) {
  if (!createdOn || !updatedOn) return false
  return createdOn !== updatedOn
}

/**
 * MAX COMMENT LENGTH CONSTANT
 * Maximum allowed comment length (10,000 characters)
 */
export const MAX_COMMENT_LENGTH = 10000
/**
 * GENERATE SCREENSHOT FILENAME
 *
 * Creates a timestamped filename for pasted clipboard images.
 * Format: screenshot-YYYYMMDD-HHmmss.ext
 *
 * @param {string} mimeType - MIME type of the image (e.g., "image/png")
 * @returns {string} Generated filename (e.g., "screenshot-20260403-103045.png")
 */
export function generateScreenshotFilename(mimeType) {
  const now = new Date()
  const timestamp = now.getFullYear().toString()
    + String(now.getMonth() + 1).padStart(2, '0')
    + String(now.getDate()).padStart(2, '0')
    + '-'
    + String(now.getHours()).padStart(2, '0')
    + String(now.getMinutes()).padStart(2, '0')
    + String(now.getSeconds()).padStart(2, '0')

  const extMap = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/bmp': 'bmp'
  }
  const ext = extMap[mimeType] || 'png'

  return `screenshot-${timestamp}.${ext}`
}
